import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Header } from './Header';
import { Footer } from './Footer';
import { SEO } from './SEO';
import {
  BookOpen, Network, CreditCard, Server, Shield, ShieldCheck, Wifi, MapPin, Terminal,
  Clock, ArrowLeft, ArrowRight, Info, AlertTriangle, CheckCircle2,
  Rocket, Infinity as InfinityIcon, Globe2, Copy, Check,
} from 'lucide-react';
import { findArticle, WIKI_CATEGORIES, articlesByCategory, WIKI_ARTICLES } from '../data/wiki';
import { smoothScrollToWithOffset } from '../lib/scroll';

const ICONS = { Network, CreditCard, Server, Shield, ShieldCheck, BookOpen, Wifi, MapPin, Terminal, Globe2 };

// Per-article SEO works through the SEO component's slugMap. Here the page
// prop is dynamic ("wiki-<slug>") so SEO.jsx looks up `seo.wiki_<slug>` in
// translations. Falls back to wiki index meta if a particular slug isn't
// pre-seeded — better than a 404-looking title.

export const WikiArticlePage = () => {
  const { slug } = useParams();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const ru = language === 'ru';
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const article = findArticle(slug || '');
  const localized = (path) => (ru ? '/ru' + path : path);

  if (!article) {
    return <Navigate to={localized('/wiki')} replace />;
  }

  const Icon = ICONS[article.icon] || BookOpen;
  const c = ru ? article.ru : article.en;
  const cat = WIKI_CATEGORIES[article.category];

  // Sidebar: aim for 5 articles total. Prefer articles in the same
  // category (newest first); if there aren't enough, top up from the
  // rest of the wiki so the sidebar is never half-empty.
  const SIDEBAR_LIMIT = 5;
  let related = (articlesByCategory()[article.category] || [])
    .filter((a) => a.slug !== article.slug)
    .slice(0, SIDEBAR_LIMIT);
  if (related.length < SIDEBAR_LIMIT) {
    const used = new Set([article.slug, ...related.map((a) => a.slug)]);
    const fillers = WIKI_ARTICLES
      .filter((a) => !used.has(a.slug))
      .sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));
    related = related.concat(fillers.slice(0, SIDEBAR_LIMIT - related.length));
  }

  // Send the user to the homepage pricing block. Mirrors Footer.jsx:
  // navigate first so React mounts the home page, then scroll once the
  // #pricing target exists in the DOM.
  const goToPricing = () => {
    navigate(localized('/'));
    setTimeout(() => smoothScrollToWithOffset('pricing', -200), 120);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO page={`wiki-${article.slug}`} />
      <Header />

      {/* Article header — uses the same max-w-6xl container as the body
          below, so the title is left-aligned with the article text rather
          than offset to the right by the wider container. */}
      <section className="relative pt-12 pb-10 bg-gradient-to-b from-slate-50 to-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:max-w-3xl">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-slate-500 mb-6">
            <Link to={localized('/wiki')} className="inline-flex items-center gap-1.5 hover:text-blue-700 transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" />
              {ru ? 'База знаний' : 'Knowledge base'}
            </Link>
            <span className="opacity-40">/</span>
            <span className="opacity-70">{cat ? cat[ru ? 'ru' : 'en'] : article.category}</span>
          </nav>

          {/* Icon as a small badge above the title — keeps title, summary
              and meta flush-left with the breadcrumb (no ml-16 jump). */}
          <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-4">
            <Icon className="w-6 h-6 text-blue-700" />
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 leading-tight tracking-tight mb-4">
            {c.title}
          </h1>

          <p className="text-lg text-slate-600 leading-relaxed mb-5">
            {c.summary}
          </p>

          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {article.readingMin} {ru ? 'мин на чтение' : 'min read'}
            </span>
            <span className="opacity-40">·</span>
            <span>{ru ? 'Обновлено ' : 'Last updated '}{article.updatedAt}</span>
          </div>
          </div>
        </div>
      </section>

      {/* Article body — main column + sticky right sidebar with other
          articles in the same category (up to 5, newest first). On mobile
          and tablet the sidebar drops below the main content. */}
      <article className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 lg:flex lg:gap-12">
          <div className="lg:flex-1 lg:max-w-3xl min-w-0">
            <div className="space-y-5">
              {c.sections.map((s, i) => <Block key={i} block={s} />)}
            </div>

          <hr className="my-12 border-slate-200" />

          {/* Promo CTA — replaces the old "Open a ticket" card.
              The wiki has many articles; each one funnels back to
              pricing. Scrolls to the homepage #pricing block since
              the React tariff state lives on the home page. */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 via-slate-800 to-blue-950 p-8 sm:p-10 text-white shadow-xl shadow-slate-900/20">
            <div className="absolute inset-0 opacity-15 pointer-events-none">
              <div className="absolute -top-10 -right-10 w-72 h-72 bg-blue-500 rounded-full blur-3xl" />
              <div className="absolute -bottom-10 -left-10 w-72 h-72 bg-cyan-500 rounded-full blur-3xl" />
            </div>
            <div className="relative">
              <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.18em] uppercase text-blue-300 mb-4">
                <Rocket className="w-3.5 h-3.5" />
                NetGrid Host
              </div>

              {/* Headline + price. Same proportion as PricingSection.jsx:
                  whole digit large, "€" and ".99" stay small + blue-300
                  so the eye lands on the "1". */}
              <h3 className="flex flex-wrap items-baseline gap-x-2 mb-3 leading-tight">
                <span className="text-2xl sm:text-3xl font-black text-white">
                  {ru ? 'VPS от' : 'VPS from'}
                </span>
                <span className="inline-flex items-baseline">
                  <span className="text-xl sm:text-2xl text-blue-300 font-semibold">€</span>
                  <span className="text-5xl sm:text-6xl font-black tracking-tight text-white leading-none">1</span>
                  <span className="text-xl sm:text-2xl text-blue-300 font-semibold">.99</span>
                </span>
                <span className="text-2xl sm:text-3xl font-black text-white">
                  {ru ? 'в месяц' : '/ month'}
                </span>
              </h3>

              <p className="text-blue-100/90 text-base leading-relaxed mb-5 max-w-xl">
                {ru
                  ? 'Безлимитный трафик, порт 1 Гбит/с и NVMe-хранилище. 12 локаций в Европе и США.'
                  : 'Unlimited traffic, a 1 Gbps port and NVMe storage. 12 locations across Europe & the US.'}
              </p>

              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-6">
                <span className="inline-flex items-center gap-1.5 text-xs text-blue-200/80">
                  <InfinityIcon className="w-3.5 h-3.5" />
                  {ru ? 'Безлимитный трафик' : 'Unlimited traffic'}
                </span>
                <span className="opacity-30">·</span>
                <span className="inline-flex items-center gap-1.5 text-xs text-blue-200/80">
                  <Network className="w-3.5 h-3.5" />
                  {ru ? 'Порт 1 Гбит/с' : '1 Gbps port'}
                </span>
                <span className="opacity-30">·</span>
                <span className="inline-flex items-center gap-1.5 text-xs text-blue-200/80">
                  <Globe2 className="w-3.5 h-3.5" />
                  {ru ? '12 локаций' : '12 locations'}
                </span>
              </div>

              <button
                onClick={goToPricing}
                className="inline-flex items-center gap-1.5 px-6 py-3 rounded-lg bg-white text-blue-950 hover:bg-blue-50 text-sm font-bold transition-colors shadow-lg shadow-blue-900/30"
              >
                {ru ? 'Посмотреть тарифы' : 'View plans'}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          </div>

          {related.length > 0 && (
            <aside className="mt-16 lg:mt-0 lg:w-72 lg:flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 mb-4">
                  {ru ? 'Читать дальше' : 'Read next'}
                </div>
                <div className="space-y-3">
                  {related.map((a) => {
                    const ric = ru ? a.ru : a.en;
                    const RIcon = ICONS[a.icon] || BookOpen;
                    return (
                      <Link
                        key={a.slug}
                        to={localized(`/wiki/${a.slug}`)}
                        className="group block rounded-xl border border-slate-200 bg-white p-4 hover:border-blue-500 hover:shadow-md hover:shadow-blue-700/5 transition-all"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition-colors">
                            <RIcon className="w-4 h-4 text-blue-700" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-slate-900 leading-snug group-hover:text-blue-700 transition-colors">
                              {ric.title}
                            </div>
                            <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-slate-500">
                              <Clock className="w-3 h-3" />
                              {a.readingMin} {ru ? 'мин' : 'min'}
                            </div>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
                <Link
                  to={localized('/wiki')}
                  className="mt-4 inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 transition-colors"
                >
                  {ru ? 'Вся база знаний' : 'All articles'}
                  <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </aside>
          )}
        </div>
      </article>

      <Footer />
    </div>
  );
};

/* ── Block renderer ───────────────────────────────────────────────── */

/**
 * Inline markdown: [text](href). Internal hrefs (start with /) become
 * <Link> (same-window navigation, no full-page reload). External hrefs
 * (http/https) become <a target="_blank" rel="noopener">. Plain segments
 * pass through as text. Returns an array of React nodes ready to inline.
 */
function renderInline(text) {
  if (!text || typeof text !== 'string' || !text.includes('[')) return text;
  const re = /\[([^\]]+)\]\(([^)]+)\)/g;
  const nodes = [];
  let last = 0;
  let i = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const [, label, href] = m;
    if (href.startsWith('/')) {
      nodes.push(
        <Link key={`l${i}`} to={href} className="text-blue-700 hover:text-blue-900 underline underline-offset-2">{label}</Link>
      );
    } else {
      nodes.push(
        <a key={`l${i}`} href={href} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:text-blue-900 underline underline-offset-2">{label}</a>
      );
    }
    last = m.index + m[0].length;
    i++;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function Block({ block }) {
  const { kind } = block;

  if (kind === 'p') {
    return <p className="text-base text-slate-700 leading-relaxed">{renderInline(block.text)}</p>;
  }
  if (kind === 'h') {
    return <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mt-8 mb-2">{renderInline(block.text)}</h2>;
  }
  if (kind === 'ul') {
    return (
      <ul className="list-disc pl-6 space-y-2 text-base text-slate-700 leading-relaxed marker:text-blue-700">
        {block.items.map((it, i) => <li key={i}>{renderInline(it)}</li>)}
      </ul>
    );
  }
  if (kind === 'ol') {
    return (
      <ol className="list-decimal pl-6 space-y-2 text-base text-slate-700 leading-relaxed marker:text-blue-700 marker:font-semibold">
        {block.items.map((it, i) => <li key={i}>{renderInline(it)}</li>)}
      </ol>
    );
  }
  if (kind === 'callout') {
    const palette = {
      info: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-700', Icon: Info },
      warn: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-700', Icon: AlertTriangle },
      good: { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'text-emerald-700', Icon: CheckCircle2 },
    }[block.tone || 'info'];
    const PIcon = palette.Icon;
    return (
      <div className={`rounded-xl border ${palette.border} ${palette.bg} p-5 my-4`}>
        <div className="flex items-start gap-3">
          <PIcon className={`w-5 h-5 ${palette.icon} flex-shrink-0 mt-0.5`} />
          <div className="flex-1">
            {block.title && <div className="font-semibold text-slate-900 mb-1">{block.title}</div>}
            <div className="text-sm text-slate-700 leading-relaxed">{renderInline(block.text)}</div>
          </div>
        </div>
      </div>
    );
  }
  if (kind === 'kv') {
    return (
      <div className="rounded-xl border border-slate-200 overflow-hidden my-4">
        <table className="w-full text-sm">
          <tbody>
            {block.rows.map(([k, v], i) => (
              <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                <td className="px-4 py-2.5 font-medium text-slate-700 border-r border-slate-200 w-1/3 align-top">{renderInline(k)}</td>
                <td className="px-4 py-2.5 text-slate-700">{renderInline(v)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (kind === 'quote') {
    return (
      <blockquote className="border-l-4 border-blue-700/60 pl-5 italic text-slate-600 my-5">
        “{block.text}”
        {block.attribution && <div className="not-italic text-sm text-slate-500 mt-1">— {block.attribution}</div>}
      </blockquote>
    );
  }
  if (kind === 'code') {
    return (
      <div className="relative my-4">
        <CopyButton text={block.text} />
        <pre className="rounded-xl bg-slate-900 text-slate-100 p-5 overflow-x-auto text-sm leading-relaxed">
          <code>{block.text}</code>
        </pre>
      </div>
    );
  }
  if (kind === 'code-tabs') {
    return <CodeTabs tabs={block.tabs || []} />;
  }
  return null;
}

/**
 * Copy-to-clipboard button — sits in the top-right of any code block.
 * Uses the modern clipboard API (HTTPS-only, but our site is always
 * HTTPS). Falls back silently if the API is unavailable.
 */
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // best-effort — ignore failure
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="absolute top-2 right-2 z-10 inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-slate-300 bg-slate-800/90 hover:bg-slate-700 hover:text-white rounded-md transition-colors backdrop-blur-sm"
      aria-label="Copy code"
      title={copied ? 'Copied' : 'Copy code'}
    >
      {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

/**
 * Tabbed code block — used for cross-distro install commands etc.
 * Default selected tab is the first (typically Ubuntu/Debian, the
 * majority of our customers). All tab labels render in the DOM, so
 * they're indexed by search engines even though only the active
 * tab's <pre> is visible.
 */
function CodeTabs({ tabs }) {
  const [active, setActive] = useState(0);
  if (!tabs || tabs.length === 0) return null;
  const safeIdx = Math.min(active, tabs.length - 1);
  return (
    <div className="my-4 rounded-xl overflow-hidden border border-slate-800">
      <div className="flex flex-wrap bg-slate-800 border-b border-slate-700">
        {tabs.map((t, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setActive(i)}
            className={
              'px-4 py-2.5 text-xs font-medium transition-colors border-r border-slate-700 last:border-r-0 ' +
              (i === safeIdx
                ? 'bg-slate-900 text-white'
                : 'text-slate-400 hover:bg-slate-700/50 hover:text-slate-200')
            }
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="relative">
        <CopyButton text={tabs[safeIdx].code} />
        <pre className="bg-slate-900 text-slate-100 p-5 overflow-x-auto text-sm leading-relaxed">
          <code>{tabs[safeIdx].code}</code>
        </pre>
      </div>
    </div>
  );
}
